/**
 * Data comparison logic for comparing Excel import data with existing RoundTable data
 */

import { transformExcelRowToRoundTable, extractRTNumberFromSubdomain } from './excelToRoundTableTransform';

export interface RoundTable {
    id: number;
    name: string;
    number: number;
    iban: string;
    bic: string;
    kontoInhaber: string;
    bankName: string;
    billingName: string;
    billingEmail: string;
    billingPlz: string;
    billingCity: string;
    billingStreet: string;
    billingStreetNumber: string;
}

export interface FieldDeviation {
    field: string;
    excelValue: any;
    currentValue: any;
}

export interface ComparisonRow {
    excelRow: Record<string, any>;
    rowIndex: number;
    status: 'new' | 'deviating' | 'unchanged';
    rtNumber?: number;
    matchedRoundTable?: RoundTable;
    deviations?: FieldDeviation[];
}

export interface ComparisonResult {
    newEntries: ComparisonRow[];
    deviatingEntries: ComparisonRow[];
}

/**
 * Extracts the RT number from the "[Club] Subdomain" field
 * Format expected: "123-subdomain"
 * @param subdomain - The subdomain string like "123-berlin"
 * @returns The extracted RT number or null if not found
 * @deprecated Use extractRTNumberFromSubdomain from excelToRoundTableTransform instead
 */
export const extractRTNumber = extractRTNumberFromSubdomain;

/**
 * Maps field names from RoundTable properties to Excel columns
 * This is the inverse of the transform mapping for comparison purposes
 */
const FIELD_MAPPINGS: Record<keyof RoundTable, string> = {
    'name': '[Club] Name',
    'number': '[Club] Subdomain', // Number is extracted from subdomain
    'iban': '[Bank Account] IBAN',
    'bic': '[Bank Account] BIC',
    'kontoInhaber': '[Bank Account] Owner',
    'bankName': '[FinOrg] Name',
    'billingName': '[Treasurer] Name',
    'billingEmail': '[Treasurer] Email',
    'billingPlz': '[Treasurer] Postal Code',
    'billingCity': '[Treasurer] City',
    'billingStreet': '[Treasurer] Street1',
    'billingStreetNumber': '[Treasurer] Street2',
    'id': '', // ID is not compared
};

/**
 * Normalizes a string for comparison by trimming whitespace and converting to lowercase
 */
const normalizeString = (value: any): string => {
    if (value === null || value === undefined) {
        return '';
    }
    return String(value).trim().toLowerCase();
};

/**
 * Compares two values and returns true if they are effectively equal
 */
const areValuesEqual = (value1: any, value2: any): boolean => {
    // Handle null/undefined cases
    if ((value1 === null || value1 === undefined) && (value2 === null || value2 === undefined)) {
        return true;
    }
    if ((value1 === null || value1 === undefined) || (value2 === null || value2 === undefined)) {
        return false;
    }

    // For strings, normalize and compare
    if (typeof value1 === 'string' || typeof value2 === 'string') {
        return normalizeString(value1) === normalizeString(value2);
    }

    // For other types, direct comparison
    return value1 === value2;
};

/**
 * Compares an Excel row with a RoundTable entry and returns deviations
 * Uses the same transformation logic as creating new entries
 * @param excelRow - The Excel row data
 * @param roundTable - The matching RoundTable entry
 * @returns Array of field deviations found
 */
const compareRows = (excelRow: Record<string, any>, roundTable: RoundTable): FieldDeviation[] => {
    const deviations: FieldDeviation[] = [];

    try {
        // Transform Excel row using the same logic as when creating entries
        const transformedData = transformExcelRowToRoundTable(excelRow);

        // Compare each field from the transformed data with the current RoundTable
        for (const [rtField, excelColumn] of Object.entries(FIELD_MAPPINGS)) {
            // Skip ID and number fields (number is matched, not compared)
            if (rtField === 'id' || rtField === 'number') {
                continue;
            }

            const fieldKey = rtField as keyof RoundTable;
            const transformedValue = transformedData[fieldKey as keyof typeof transformedData];
            const currentValue = roundTable[fieldKey];

            if (!areValuesEqual(transformedValue, currentValue)) {
                deviations.push({
                    field: excelColumn,
                    excelValue: transformedValue,
                    currentValue,
                });
            }
        }
    } catch (error) {
        // If transformation fails, we can't compare properly
        console.error('Error transforming Excel row for comparison:', error);
    }

    return deviations;
};

/**
 * Compares Excel rows with RoundTable data and identifies new and deviating entries
 * @param excelData - Array of Excel row data
 * @param roundTablesData - Array of existing RoundTable entries
 * @returns Comparison result with new and deviating entries
 */
export const compareData = (
    excelData: Record<string, any>[],
    roundTablesData: RoundTable[]
): ComparisonResult => {
    const newEntries: ComparisonRow[] = [];
    const deviatingEntries: ComparisonRow[] = [];

    // Create a map of RT number to RoundTable for efficient lookup
    const rtNumberMap = new Map<number, RoundTable>();
    roundTablesData.forEach(rt => {
        rtNumberMap.set(rt.number, rt);
    });

    excelData.forEach((excelRow, index) => {
        const subdomain = excelRow['[Club] Subdomain'];
        const rtNumber = extractRTNumber(subdomain);

        if (rtNumber === null) {
            // No RT number found, consider as new
            newEntries.push({
                excelRow,
                rowIndex: index,
                status: 'new',
            });
            return;
        }

        const matchedRoundTable = rtNumberMap.get(rtNumber);

        if (!matchedRoundTable) {
            // No matching RoundTable found, consider as new
            newEntries.push({
                excelRow,
                rowIndex: index,
                status: 'new',
                rtNumber,
            });
            return;
        }

        // Compare the rows
        const deviations = compareRows(excelRow, matchedRoundTable);

        if (deviations.length > 0) {
            // Found deviations
            deviatingEntries.push({
                excelRow,
                rowIndex: index,
                status: 'deviating',
                rtNumber,
                matchedRoundTable,
                deviations,
            });
        }
        // If no deviations, we don't add the row (as per requirement)
    });

    return {
        newEntries,
        deviatingEntries,
    };
};
